/**
 * Tiny relative-time formatter — "3h ago", "2d ago" etc. Used by the leads
 * list to render `createdAt` compactly. Kept dependency-free (no
 * `date-fns`, no `dayjs`) because the SPA's bundle stays small.
 *
 * Returns the absolute date when the gap is greater than 30 days — past
 * that point a relative format ("3 months ago") loses precision and the
 * operator wants the actual date for sorting / scanning.
 */

const SECOND = 1000;
const MINUTE = 60 * SECOND;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

export function formatRelativeTime(iso: string, now: Date = new Date()): string {
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return iso;
  const delta = now.getTime() - then.getTime();
  if (delta < 0) {
    // Future timestamp (clock skew etc.) — fall back to absolute display.
    return then.toLocaleDateString();
  }
  if (delta < MINUTE) return "just now";
  if (delta < HOUR) return `${Math.floor(delta / MINUTE)}m ago`;
  if (delta < DAY) return `${Math.floor(delta / HOUR)}h ago`;
  if (delta < 30 * DAY) return `${Math.floor(delta / DAY)}d ago`;
  return then.toLocaleDateString();
}
